/**
 * REPORT TABS
 *
 * Sub-tab bar rendered at the top of each report level. The tab list is
 * level-specific:
 *
 *   project  → Overview · Trends · Releases Comparison · Defects · People · Traceability
 *   release  → Overview · Trends · Defects · People · Top Failing
 *   scenario → Overview · Trends · Defects · History
 *
 * Each tab button dispatches `setReportTab(id)`. Tabs that don't yet have
 * implementations are rendered as disabled with a "Soon" badge so the roadmap
 * is visible to users and easy to wire up incrementally.
 */

import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { token } from '@atlaskit/tokens'
import {
  selectCurrentReportTab,
  setReportTab
} from '../../store/slices/reportsSlice'

// Tabs that are implemented today. Others render disabled with a "Soon" badge.
const IMPLEMENTED = new Set([
  'overview',
  'trends',
  'defects',
  'people',
  'traceability',
  'topFailing',
  'history',
  'releasesComparison'
])

const TAB_CONFIG = {
  project: [
    { id: 'overview', label: 'Overview' },
    { id: 'trends', label: 'Trends' },
    { id: 'releasesComparison', label: 'Releases' },
    { id: 'defects', label: 'Defects' },
    { id: 'people', label: 'People' },
    { id: 'traceability', label: 'Traceability' }
  ],
  release: [
    { id: 'overview', label: 'Overview' },
    { id: 'trends', label: 'Trends' },
    { id: 'defects', label: 'Defects' },
    { id: 'people', label: 'People' },
    { id: 'topFailing', label: 'Top Failing' }
  ],
  scenario: [
    // The per-run breakdown is already shown at the bottom of the Overview tab
    // (`ScenarioReport`'s "Test Runs Breakdown" table), so no separate Runs tab here.
    { id: 'overview', label: 'Overview' },
    { id: 'trends', label: 'Trends' },
    { id: 'defects', label: 'Defects' },
    { id: 'history', label: 'History' }
  ]
}

const SubTabButton = ({ label, isActive, onClick, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={{
      position: 'relative',
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: isActive ? 600 : 400,
      border: 'none',
      background: 'transparent',
      color: isActive
        ? token('color.text.selected', '#0052CC')
        : disabled
          ? token('color.text.disabled', '#A5ADBA')
          : token('color.text.subtle', '#6B778C'),
      borderBottom: isActive
        ? `2px solid ${token('color.border.selected', '#0052CC')}`
        : '2px solid transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'color 0.15s ease, border-color 0.15s ease',
      marginRight: '2px'
    }}
  >
    {label}
    {disabled && (
      <span
        style={{
          marginLeft: '6px',
          padding: '1px 5px',
          fontSize: '9px',
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          backgroundColor: token('color.background.neutral', '#F4F5F7'),
          color: token('color.text.subtle', '#6B778C'),
          borderRadius: '3px',
          verticalAlign: 'middle'
        }}
      >
        Soon
      </span>
    )}
  </button>
)

const ReportTabs = ({ level }) => {
  const dispatch = useDispatch()
  const currentTab = useSelector(selectCurrentReportTab)

  const tabs = TAB_CONFIG[level] || []
  if (!tabs.length) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        marginBottom: '16px',
        borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
      }}
    >
      {tabs.map((tab) => (
        <SubTabButton
          key={tab.id}
          label={tab.label}
          isActive={currentTab === tab.id}
          disabled={!IMPLEMENTED.has(tab.id)}
          onClick={() => dispatch(setReportTab(tab.id))}
        />
      ))}
    </div>
  )
}

export default ReportTabs
